import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Edit, X, Save } from "lucide-react";
import { useRef, useEffect } from "react";

interface AvatarData {
  avatar_url?: string;
  first_name: string;
  last_name: string;
  email: string;
  role: string;
  status: string;
}

interface AvatarSectionProps {
  avatarData: AvatarData;
  onAvatarDataChange: (data: AvatarData) => void;
  isEditing?: boolean;
  onEditToggle?: () => void;
  onCancelEdit?: () => void;
  onSave?: () => void;
  isSaving?: boolean;
}

const AvatarSection = ({ 
  avatarData, 
  onAvatarDataChange, 
  isEditing = false,
  onEditToggle,
  onCancelEdit,
  onSave,
  isSaving = false
}: AvatarSectionProps) => {
  const formRef = useRef<HTMLFormElement>(null);

  const handleInputChange = (field: keyof AvatarData) => (value: string) => {
    onAvatarDataChange({
      ...avatarData,
      [field]: value
    });
  };

  const handleSave = () => {
    if (onSave) {
      onSave();
    }
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isEditing && onSave) {
      onSave();
    }
  };

  // Gestione tasto Invio
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && isEditing && onSave) {
        e.preventDefault();
        onSave();
      }
    };

    if (isEditing) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isEditing, onSave]);

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  const getDisplayName = (firstName: string, lastName: string) => {
    return `${firstName} ${lastName}`.trim() || 'Utente';
  };

  return (
    <form ref={formRef} onSubmit={handleFormSubmit} className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900 mb-2">
            <div className="h-5 w-5 rounded-full bg-gradient-to-br from-blue-500 to-purple-600"></div>
            Avatar e Informazioni Principali
          </h3>
          <p className="text-sm text-gray-600">
            Gestisci il tuo avatar e le informazioni di base
          </p>
        </div>
        {onEditToggle && onCancelEdit && (
          <div className="flex gap-2 min-h-[40px] items-center">
            {isEditing ? (
              <>
                <Button
                  type="button"
                  variant="outline"
                  onClick={onCancelEdit}
                  className="flex items-center gap-2"
                  disabled={isSaving}
                >
                  <X className="h-4 w-4" />
                  Annulla
                </Button>
                {onSave && (
                  <Button
                    type="submit"
                    onClick={handleSave}
                    disabled={isSaving}
                    className="flex items-center gap-2"
                  >
                    {isSaving ? (
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    ) : (
                      <Save className="h-4 w-4" />
                    )}
                    {isSaving ? 'Salvando...' : 'Salva'}
                  </Button>
                )}
              </>
            ) : (
              <Button
                type="button"
                onClick={onEditToggle}
                className="flex items-center gap-2"
              >
                <Edit className="h-4 w-4" />
                Modifica
              </Button>
            )}
          </div>
        )}
      </div>

      <Card className="border-0 shadow-none bg-gray-50">
        <CardContent className="p-6">
          <div className="flex items-start space-x-6">
            {/* Avatar */}
            <div className="flex-shrink-0">
              <Avatar className="h-20 w-20">
                <AvatarImage src={avatarData.avatar_url} alt={getDisplayName(avatarData.first_name, avatarData.last_name)} />
                <AvatarFallback className="text-lg font-semibold bg-gradient-to-br from-blue-500 to-purple-600 text-white">
                  {getInitials(avatarData.first_name, avatarData.last_name)}
                </AvatarFallback>
              </Avatar>
            </div>

            {/* Profile Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-3 mb-2">
                <h1 className="text-2xl font-bold text-gray-900">
                  {getDisplayName(avatarData.first_name, avatarData.last_name)}
                </h1>
                <Badge variant="secondary" className="bg-green-100 text-green-800">
                  {avatarData.status}
                </Badge>
              </div>
              
              <p className="text-gray-600 mb-3">
                {avatarData.email}
              </p>
              
              <div className="flex items-center space-x-4">
                <Badge variant="outline" className="border-blue-200 text-blue-700">
                  {avatarData.role}
                </Badge>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </form>
  );
};

export default AvatarSection;
